/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/Sections/Skills/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useEffect, useRef } from "react";
import styles from "./Skills.module.css";
import { useTranslation } from "react-i18next";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { getCompanyFieldValue } from "../../../utils/common";

const Skills = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t } = useTranslation();
  const prevHasErrorsRef = useRef(false);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    maquinariaYEquipo: "",
    procesosYEspecialidades: "",
    clientes: "",
  });

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.skills.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div
        className={`${styles.inputRow} ${styles.twoInputsRow} ${styles.lastRow}`}
      >
        <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.skills.machineryAndEquipmentLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={`${styles.input} ${
                getFieldValidationState(
                  "maquinariaYEquipo",
                  getCompanyFieldValue(
                    company,
                    "maquinariaYEquipo",
                    selectedProfile,
                    "",
                  ),
                ) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.skills.machineryAndEquipmentPlaceholder",
              )}
              value={getCompanyFieldValue(
                company,
                "maquinariaYEquipo",
                selectedProfile,
                "",
              )}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange("maquinariaYEquipo", value);
                handleFieldChange("maquinariaYEquipo", value);
              }}
              maxLength={300}
            />
            {getErrorMessage("maquinariaYEquipo") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("maquinariaYEquipo")}
              </p>
            )}
          </div>
        </div>
        <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.skills.processesAndSpecialtiesLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={`${styles.input} ${
                getFieldValidationState(
                  "procesosYEspecialidades",
                  getCompanyFieldValue(
                    company,
                    "procesosYEspecialidades",
                    selectedProfile,
                    "",
                  ),
                ) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.skills.processesAndSpecialtiesPlaceholder",
              )}
              value={getCompanyFieldValue(
                company,
                "procesosYEspecialidades",
                selectedProfile,
                "",
              )}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange("procesosYEspecialidades", value);
                handleFieldChange("procesosYEspecialidades", value);
              }}
              maxLength={300}
            />
            {getErrorMessage("procesosYEspecialidades") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("procesosYEspecialidades")}
              </p>
            )}
          </div>
        </div>
        <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.skills.clientsLabel")}
          </label>
          <input
            type="text"
            className={`${styles.input} ${
              getFieldValidationState(
                "clientes",
                getCompanyFieldValue(company, "clientes", selectedProfile, ""),
              ) === "error"
                ? styles.inputError
                : ""
            }`}
            placeholder={t("components.skills.clientsPlaceholder")}
            value={getCompanyFieldValue(
              company,
              "clientes",
              selectedProfile,
              "",
            )}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange("clientes", value);
              handleFieldChange("clientes", value);
            }}
            maxLength={300}
          />
          {getErrorMessage("clientes") && (
            <p className={styles.errorMessage}>{getErrorMessage("clientes")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Skills;
